import React, { useEffect, useState } from "react";
import {
  Button,
  Content,
  Dialog,
  DialogContainer,
  Heading,
  Flex,
  Divider,
  View,
  Text,
  ListView,
  Item,
  Picker,
  SearchField,
} from "@adobe/react-spectrum";
import { reassignCallbackApi, getAllCallbackQueuesApi } from "../../api/session-api";
import CallbackSuccessCard from "./CallbackSuccessCard";
import Close from "@spectrum-icons/workflow/Close";
import { toast } from "react-toastify";
import Emitter from "../../emitter";
import { TELEMETRY_ERROR } from "../../constants/events";
import { API_CALLBACK_DASHBOARD } from "../../constants/events";


interface ReassignCallbackDialogProps {
  isOpen: boolean;
  onClose: () => void;
  callbackData?: any;
  onReassign?: () => void;
  userEmail?: string;
}


const ReassignCallbackDialog: React.FC<ReassignCallbackDialogProps> = ({
  isOpen,
  onClose,
  callbackData,
  onReassign,
  userEmail,
}) => {
  const [callbackQueues, setCallbackQueues] = useState<any[]>([]);
  const [selectedQueue, setSelectedQueue] = useState<string>("");
  const [selectedAgent, setSelectedAgent] = useState<string>("");
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [searchUser, setSearchUser] = useState<string>("");
  // const [reassignReason, setReassignReason] = useState("");
  const [isLoading] = useState(false);
  const [showSuccessCard, setShowSuccessCard] = useState(false);
  const [successData, setSuccessData] = useState<any>(null);

  // console.log("callbackData", callbackData);


  const handleGetAllCallbackQueues = async () => {
    const data = {
      phoneNumber: callbackData?.phoneNumber,
      contactId: callbackData?.contactId
    }
    try {
      const response = await getAllCallbackQueuesApi(data);
      if(response.status === "SUCCESS") {
        setCallbackQueues(response.data || [] );
      }
    } catch (error) {
      console.error("Error in getting callback queues", error);
      toast.error("Failed to get callback queues");
    }
  };

  useEffect(() => {
    if(callbackData?.phoneNumber && callbackData?.contactId) {
      handleGetAllCallbackQueues();
    }
  }, [callbackData]);

  const handleQueueSelection = (queueId: React.Key | null) => {
    const queueIdStr = queueId as string;
    setSelectedQueue(queueIdStr);
    setSelectedAgent(""); 
    setSearchUser(""); 
    
    if (queueIdStr) {
      const selectedQueueData = callbackQueues.find(queue => queue.queue_id === queueIdStr);
      if (selectedQueueData) {
        setAvailableUsers(selectedQueueData.users);
      } else {
        setAvailableUsers([]);
      }
    } else {
      setAvailableUsers([]);
    }
  };

  // Filter users based on search query
  const filteredUsers = availableUsers?.filter(user => {
    if (!searchUser.trim()) return true;
    
    const query = searchUser.toLowerCase();
    const fullName = user.fullName?.toLowerCase() || "";
    const userName = user.userName?.toLowerCase() || "";
    
    return fullName.includes(query) || userName.includes(query);
  }).sort((a, b) => {
    const nameA = a.fullName?.toLowerCase() || "";
    const nameB = b.fullName?.toLowerCase() || "";
    return nameA.localeCompare(nameB);
  });

  const handleReassign = async () => {
    
    // handleGetAllCallbackQueues(data);
    if (!selectedAgent 
      // || !reassignReason?.trim()
    ) {
      return;
    }

    // setIsLoading(true);
    
    try {
      const selectedAgentData = availableUsers.find(user => user?.userId === selectedAgent);
      const selectedQueueData = callbackQueues.find(queue => queue.queue_id === selectedQueue);
      const fullName = selectedAgentData?.fullName +" "+ `(${selectedAgentData?.userName?.split("@")[0]})`;
      const payload = {
        contactId: callbackData?.contactId,
        phoneNumber: callbackData?.phoneNumber,
        reAssignQueue : selectedQueueData?.queue_name,
        reAssignQueueId: selectedQueueData?.queue_id,
        reAssignEngineerLdap: selectedAgentData?.userName,
        reAssignEngineerFullName : fullName,
        // reason: reassignReason,

      }      
      const response = await reassignCallbackApi(payload)
      if(response.status === "SUCCESS") {
        // toast.success("Callback reassigned successfully");
        const successDetails = {
          agentName: `${selectedAgentData?.fullName || '-'} (${selectedAgentData?.userName?.split("@")[0]})`,
          // reason: reassignReason,
        };
        Emitter.emit(TELEMETRY_ERROR, {
          type: "notif",
          chan: TELEMETRY_ERROR,
          body: {
            type: API_CALLBACK_DASHBOARD,
            message: "Callback Reassigned successfully by " + userEmail,
            data : payload
          },
        });
        setSuccessData(successDetails);
        setShowSuccessCard(true);
      } else {
        toast.error("Failed to reassign callback");
        handleClose();

      }
      if (onReassign) {
      onReassign()        
      }
        
    } catch (error) {
      console.error("Error reassigning callback:", error);
      toast.error("Failed to reassign callback");
      handleClose();
    } finally {
      // setIsLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedAgent("");
    setSelectedQueue("");
    setAvailableUsers([]);
    setSearchUser("");
    // setReassignReason("");
    setShowSuccessCard(false);
    setSuccessData(null);
    onClose();
  };

  const handleSuccessCardClose = () => {
    setShowSuccessCard(false);
    setSuccessData(null);
    handleClose(); 
  };

  // Determine if reassign button should be enabled
  const isReassignDisabled = !selectedAgent 
  // || !reassignReason?.trim();


  return (
    <>
      {/* Success Card */}
      <CallbackSuccessCard
        isOpen={showSuccessCard}
        onClose={handleSuccessCardClose}
        title="Callback is reassigned"
        subtitle="Your callback has been reassigned with the below details"
        details={successData}
        type="reassign"
        prevAgentName={callbackData?.callbackAttributes?.ASSIGNED_ENGINEER_FULLNAME}
        newAgentName={successData?.agentName}
      />

      {/* Main Dialog */}
      <DialogContainer onDismiss={handleClose}>
        {isOpen && !showSuccessCard && (
        <Dialog 
          size="M" 
          UNSAFE_className="reassign-callback-dialog"
          UNSAFE_style={{
            borderRadius: '12px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.15)'
          }}
        >
          <Heading level={4}>
            <Flex justifyContent="space-between" alignItems="center">
              <span style={{ 
                fontSize: '20px', 
                fontWeight: '700', 
                color: '#333',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                
                Reassign Callback
              </span>
              <span
                style={{ 
                  cursor: "pointer", 
                  fontSize: "24px", 
                  color: "#999",
                  padding: "8px",
                  borderRadius: "50%",
                  transition: "all 0.2s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "32px",
                  height: "32px"
                }}
                onClick={handleClose}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f0f0f0';
                  e.currentTarget.style.color = '#666';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#999';
                }}
              >
                <Close />
              </span>
            </Flex>
          </Heading>
          <Divider marginBottom="size-0" UNSAFE_style={{ borderColor: '#e0e0e0' }} />

          <Content>
            <View padding="size-200">
              {/* Search Section */}
              <View marginBottom="size-100">
                <Text UNSAFE_style={{ 
                  fontSize: '16px', 
                  fontWeight: '600', 
                  color: '#333',
                  marginBottom: '12px',
                  display: 'block'
                }}>
                  Who would you like to reassign the callback?
                </Text>
                <Flex direction="column" gap="size-150" marginTop="size-150">
                  <View>
                    <Text UNSAFE_style={{ 
                      fontSize: '14px', 
                      fontWeight: '500', 
                      color: '#333',
                      marginBottom: '8px',
                      display: 'block'
                    }}>
                      Select Queue
                    </Text>
                    <Picker
                      items={callbackQueues}
                      selectedKey={selectedQueue}
                      onSelectionChange={handleQueueSelection}
                      width="100%"
                      placeholder="Select a queue"
                      isLoading={callbackQueues?.length === 0} // as queues will always come in API response
                    >
                      {(item) => <Item key={item.queue_id}>{item.queue_name}</Item>}
                    </Picker>
                  </View>
                  
                  {/* Users List - only show when queue is selected */}
                  {selectedQueue && availableUsers.length > 0 && (
                    <View>
                      <Text UNSAFE_style={{ 
                        fontSize: '12px', 
                        fontWeight: '500', 
                        color: '#333',
                        marginBottom: '8px',
                        display: 'block'
                      }}>
                        Select User
                      </Text>
                      
                      {/* Search Field */}
                      <View>
                        <SearchField
                          placeholder="Search users"
                          value={searchUser}
                          onChange={setSearchUser}
                          width="100%"
                          UNSAFE_style={{
                            marginBottom: '8px'
                          }}
                        />
                      </View>

                      {/* User List */}
                      {filteredUsers.length > 0 ? (
                        <ListView
                          items={filteredUsers}
                          selectedKeys={selectedAgent ? [selectedAgent] : []}
                          selectionMode="single"
                          onSelectionChange={(keys) => {
                            const selectedKey = Array.from(keys)[0] as string;
                            setSelectedAgent(selectedKey || "");
                          }}
                          height="size-3400"
                          UNSAFE_style={{
                            border: '1px solid #d1d1d1',
                            borderRadius: '6px',
                            backgroundColor: '#fafafa'
                          }}
                        >
                          {(item) => (
                            <Item key={item.userId} textValue={item.fullName}>
                              <Text>{item.fullName}</Text>
                              <Text slot="description" UNSAFE_style={{ fontSize: '12px', color: '#666' }}>
                                {item.userName}
                              </Text>
                            </Item>
                          )}
                        </ListView>
                      ) : (
                        <View UNSAFE_style={{
                          border: '1px solid #d1d1d1',
                          borderRadius: '6px',
                          backgroundColor: '#fafafa',
                          padding: '20px',
                          textAlign: 'center'
                        }}>
                          <Text UNSAFE_style={{ 
                            fontSize: '14px', 
                            color: '#666',
                            fontStyle: 'italic'
                          }}>
                            {searchUser?.trim() ? 'No users found matching your search' : 'No users available'}
                          </Text>
                        </View>
                      )}
                    </View>
                  )}
                  
                  {/* Show message when queue is selected but no users */}
                  {selectedQueue && availableUsers.length === 0 && (
                    <View>
                      <Text UNSAFE_style={{ 
                        fontSize: '14px', 
                        color: '#666',
                        fontStyle: 'italic',
                        textAlign: 'center',
                        padding: '20px'
                      }}>
                        No users available in the selected queue
                      </Text>
                    </View>
                  )}
                </Flex>
              </View>

             
              {/* Hidden Reason Section */}
              {/* <View marginBottom="size-300">
                <Text 
                  marginBottom="size-100" 
                  UNSAFE_style={{ 
                    fontSize: '12px', 
                    fontWeight: '400',
                    lineHeight: '1.3',
                    color: '#535353',
                  }}
                >
                  Reason for reassign
                </Text>
                <TextArea
                  placeholder="Please provide a reason for reassigning this callback..."
                  value={reassignReason}
                  onChange={setReassignReason}
                  width="100%"
                  height="size-1200"
                  UNSAFE_style={{
                    borderRadius: '4px',
                    // border: reassignReason.trim() ? '1px solid #4caf50' : undefined,
                  }}
                />
                {reassignReason?.trim() && (
                  <Text UNSAFE_style={{ 
                    fontSize: '12px', 
                    color: '#4caf50',
                    marginTop: '4px'
                  }}>
                    ✓ Reason provided
                  </Text>
                )}
              </View> */}

              {/* Action Buttons */}
              <Flex justifyContent="end" gap="size-150" marginTop="size-400">
                <Button 
                  variant="secondary" 
                  onPress={handleClose}
                  UNSAFE_style={{
                    // borderRadius: '6px',
                    minWidth: '100px'
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  variant="cta" 
                  onPress={handleReassign}
                  isDisabled={isReassignDisabled || isLoading}
                  UNSAFE_style={{
                    // borderRadius: '6px',
                    minWidth: '120px',
                    opacity: (isReassignDisabled || isLoading) ? 0.6 : 1
                  }}
                >
                  {isLoading ? 'Reassigning...' :  'Reassign Callback'}
                </Button>
              </Flex>
            </View>
          </Content>
        </Dialog>
        )}
      </DialogContainer>
    </>
  );
};

export default ReassignCallbackDialog;
